(() => {
  'use strict';
  if (window.AWConfiguratorP0Guard?.version) return;

  const CONFIG_KEY = 'aw_airsoft_configurator';

  const readJson = (key, fallback = {}) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };

  const authoritative = (root) => {
    const source = root?.querySelector?.('[data-aw-config-source]');
    const mode = String(root?.dataset?.awIndexMode || source?.dataset?.awIndexMode || '');
    return /^precomputed(?:-|$)/.test(mode);
  };

  const sourceNodeFor = (root, variantId) => Array.from(root.querySelectorAll('[data-aw-config-product]'))
    .find((node) => String(node.dataset.variantId || '') === String(variantId));

  const normalizeCopy = (root) => {
    if (!root) return;
    const suggestionBadge = root.querySelector('[data-aw-config-suggestions] .aw-configurator__suggestions-heading > span');
    if (suggestionBadge?.textContent.trim() === 'Compatibilité vérifiée') {
      suggestionBadge.textContent = 'Correspondances AW — à vérifier';
    }

    const loading = root.querySelector('[data-aw-config-loading]');
    if (loading?.textContent.trim() === 'Analyse des produits compatibles…') {
      loading.textContent = 'Analyse des correspondances AW…';
    }

    const stepOneText = root.querySelector('[data-aw-step="1"] .aw-configurator__step-heading p');
    if (stepOneText?.textContent.trim() === 'Le configurateur utilise les données de compatibilité AW pour filtrer tout le catalogue.') {
      stepOneText.textContent = 'Le configurateur utilise les données Compatibility AW disponibles pour filtrer les produits référencés.';
    }

    const stepTwoText = root.querySelector('[data-aw-step="2"] .aw-configurator__step-heading p');
    if (stepTwoText?.textContent.trim() === 'Ces informations améliorent le classement des composants compatibles.') {
      stepTwoText.textContent = 'Ces informations améliorent le classement des correspondances AW selon les données disponibles.';
    }

    const integrationText = root.querySelector('.aw-configurator__integration-note span');
    if (integrationText?.textContent.trim() === 'Vos choix seront réutilisés automatiquement sur les fiches produit compatibles.') {
      integrationText.textContent = 'Vos choix seront réutilisés sur les fiches produit disposant de données Compatibility AW.';
    }

    const stepThreeText = root.querySelector('[data-aw-step="3"] .aw-configurator__step-heading p');
    if (stepThreeText?.textContent.trim() === 'Les résultats sont classés selon la compatibilité et votre objectif.') {
      stepThreeText.textContent = 'Les résultats sont classés selon les données AW disponibles et votre objectif.';
    }
  };

  const sanitize = (root) => {
    if (!root || root.dataset.awReady !== 'true' || !authoritative(root)) {
      return { removed: 0, deferred: true };
    }

    const key = root.dataset.storageKey || CONFIG_KEY;
    const beforeState = readJson(key, {});
    const before = Array.isArray(beforeState.selected) ? beforeState.selected.map(String) : [];
    if (!before.length) return { removed: 0 };

    const ghosts = before.filter((variantId) => !sourceNodeFor(root, variantId));
    if (!ghosts.length) {
      root.dataset.awGhostSelectionRemoved = '0';
      return { removed: 0 };
    }

    const productRoot = root.querySelector('[data-aw-config-products]');
    if (!productRoot) return { removed: 0, deferred: true };

    ghosts.forEach((variantId) => {
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.hidden = true;
      trigger.dataset.awConfigToggle = variantId;
      trigger.dataset.awP0GhostRemoval = 'true';
      productRoot.appendChild(trigger);
      trigger.click();
      trigger.remove();
    });

    const afterState = readJson(key, {});
    const after = new Set(Array.isArray(afterState.selected) ? afterState.selected.map(String) : []);
    const removedIds = ghosts.filter((variantId) => !after.has(variantId));
    const removed = removedIds.length;
    root.dataset.awGhostSelectionRemoved = String(removed);

    if (removed > 0) {
      const notice = root.querySelector('[data-aw-config-notice]');
      if (notice) {
        notice.hidden = false;
        notice.textContent = `${removed} composant${removed > 1 ? 's' : ''} retiré${removed > 1 ? 's' : ''} de la configuration : ${removed > 1 ? 'ces références ne sont' : 'cette référence n’est'} plus disponible${removed > 1 ? 's' : ''} dans l’index AW.`;
        notice.classList.remove('is-error');
        notice.classList.remove('is-success');
      }
      window.dispatchEvent(new CustomEvent('aw:configuration-sanitized', {
        detail: { reason: 'missing-index-variant', removed }
      }));
    }

    return { removed };
  };

  const initRoot = (root) => {
    if (!root || root.dataset.awP0GuardObserved === 'true') return;
    root.dataset.awP0GuardObserved = 'true';
    normalizeCopy(root);

    const run = () => {
      normalizeCopy(root);
      return sanitize(root);
    };

    if (root.dataset.awReady === 'true') run();
    else if ('MutationObserver' in window) {
      const observer = new MutationObserver(() => {
        if (root.dataset.awReady !== 'true') return;
        observer.disconnect();
        run();
      });
      observer.observe(root, { attributes: true, attributeFilter: ['data-aw-ready'] });
    }
  };

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-aw-configurator]')) roots.push(scope);
    scope.querySelectorAll?.('[data-aw-configurator]').forEach((root) => roots.push(root));
    roots.forEach(initRoot);
  };

  const sanitizeAll = () => document.querySelectorAll('[data-aw-configurator]').forEach((root) => {
    normalizeCopy(root);
    sanitize(root);
  });

  window.addEventListener('aw:compatibility-index-ready', sanitizeAll);
  window.addEventListener('aw:context-changed', () => queueMicrotask(sanitizeAll));
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.AWConfiguratorP0Guard = {
    version: '1.0.1',
    init,
    sanitize,
    authoritative
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
